import { RootState } from '@/store/store'
import { AgentConversation, MatterConversationListEntry } from './slice'
import { createSelector } from '@reduxjs/toolkit'

/**
 * Select: Agent Conversation From Id
 * Returns the agent conversation from the state if it exists.
 */
export const selectAgentConversationFromId = (state: RootState, props: { conversationId: string | undefined }): AgentConversation | null => {
  const { conversationId } = props
  return state.agentConversationsState.conversations[conversationId ?? ''] ?? null
}

/**
 * Select: Agent Conversation Exists
 * Returns true if the agent conversation exists in the state
 */
export const selectAgentConversationExists = createSelector(selectAgentConversationFromId, (conversation) => conversation !== null)

/**
 * Select: Agent Conversation Title
 * Retrieves the title of an agent conversation.
 */
export const selectAgentConversationTitle = createSelector(selectAgentConversationFromId, (conversation) => conversation?.title ?? null)

/**
 * Select: Agent Conversation Loading
 * Returns true if the agent conversation is loading.
 */
export const selectAgentConversationLoading = createSelector(selectAgentConversationFromId, (conversation) => conversation?.isLoading ?? false)

/**
 * Select: Agent Conversation Detail Loaded
 * Returns true after full conversation data has been fetched at least once.
 */
export const selectAgentConversationDetailLoaded = createSelector(selectAgentConversationFromId, (conversation) => {
  return Boolean(conversation && conversation.lastRefresh !== null)
})

/**
 * Select: Agent Conversation Matter Id
 * Returns the matter id for the agent conversation.
 */
export const selectAgentConversationMatterId = createSelector(selectAgentConversationFromId, (conversation) => conversation?.matter_id ?? null)

/**
 * Select: Agent Conversation Load Error
 * Returns the load error for the agent conversation if it exists.
 */
export const selectAgentConversationLoadError = createSelector(selectAgentConversationFromId, (conversation) => conversation?.loadError ?? null)

export const selectAgentConversationActiveContentViewer = (state: RootState, props: { conversationId: string | undefined }) => {
  const { conversationId } = props
  return state.agentConversationsState.activeContentViewer[conversationId ?? ''] ?? null
}

export const selectAgentConversationHasActiveContentViewer = createSelector(
  selectAgentConversationActiveContentViewer,
  (activeContentViewer) => activeContentViewer !== null
)

/**
 * Select: Matter Conversation List Entry
 * Returns the per-matter conversation-list fetch state, or null if the matter has never been fetched.
 */
export const selectMatterConversationListEntry = (state: RootState, props: { matterId: string | null }): MatterConversationListEntry | null => {
  const { matterId } = props
  if (!matterId) return null
  return state.agentConversationsState.conversationListByMatterId[matterId] ?? null
}

/**
 * Select: Matter Conversations Loading
 * Returns true while the matter's conversation list is being fetched.
 */
export const selectMatterConversationsLoading = (state: RootState, props: { matterId: string | null }): boolean =>
  selectMatterConversationListEntry(state, props)?.loading ?? false

/**
 * Select: Matter Conversations Error
 * Returns true if the matter's last conversation-list fetch failed.
 */
export const selectMatterConversationsError = (state: RootState, props: { matterId: string | null }): boolean =>
  selectMatterConversationListEntry(state, props)?.error ?? false

/**
 * Select: Matter Conversations Initial Load Complete
 * Returns true once the matter's conversation list has loaded at least once.
 */
export const selectMatterConversationsInitialLoadComplete = (state: RootState, props: { matterId: string | null }): boolean =>
  selectMatterConversationListEntry(state, props)?.initialLoadComplete ?? false
